import { useEffect, useRef, useState } from "react";

/**
 * Custom hook to trigger animations when element enters viewport.
 *
 * Safari iOS fixes applied:
 * - threshold lowered to 0.15 (50% was too high for tall sections on small viewports)
 * - empty deps array prevents observer from being recreated on every render
 * - observer.disconnect() called immediately after first trigger (fire-once)
 *
 * @param {IntersectionObserverInit} [options]
 * @returns {{ ref: React.MutableRefObject<any>, isVisible: boolean }}
 */
export const useScrollAnimation = (options = {}) => {
    const ref = useRef(null);
    const [isVisible, setIsVisible] = useState(false);
    const optionsRef = useRef(options);

    useEffect(() => {
        const element = ref.current;
        if (!element) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    observer.disconnect();
                }
            },
            {
                threshold: 0.15,
                rootMargin: "0px",
                ...optionsRef.current,
            },
        );

        observer.observe(element);

        return () => observer.disconnect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return { ref, isVisible };
};

export default useScrollAnimation;
